import { useCallback } from "react";
import { useSelector } from "react-redux";
import { AppState } from "../store/rootReducer";
import { useEmployeeRolePermissions } from "../Data/EmployeeRoleData";
import { IApplicationStatusWorkflowItem } from "../Interfaces/Application/IApplicationStatusWorkflowItem";

/**
 * Hook that provides the logic for determining which ApplicationStatusWorkflowItems
 * are available for a given status and quote type. Replicates the filtering logic from
 * StatusWorkflowComponent so it can be reused anywhere (queue rows, detail views, etc.).
 *
 * Returns a function that, given a statusId and quoteType (as a number), returns the
 * list of workflow items the current user is permitted to trigger.
 *
 * Optionally pass `queueRowOnly: true` to filter to only items with showInApplicationQueueRow.
 */
export const useAvailableStatusWorkflowActions = () => {
  const applicationStatusWorkflows = useSelector((state: AppState) => state.applications.applicationStatusWorkflows);
  const [, , canAct] = useEmployeeRolePermissions();

  const getAvailableActions = useCallback((
    statusId: string,
    quoteType: number,
    options?: { queueRowOnly?: boolean }
  ): IApplicationStatusWorkflowItem[] => {
    const workflow = applicationStatusWorkflows.find(
      asw => Number(asw.applicationQuoteType) === quoteType
    );
    if (!workflow) return [];

    return workflow.applicationStatusWorkflowItems.filter(aswi =>
      aswi.currentStatusId?.toUpperCase() === statusId.toUpperCase() &&
      aswi.triggeredBy === 'Button' &&
      (!options?.queueRowOnly || aswi.showInApplicationQueueRow === true) &&
      canAct('Actions', aswi.label!)
    );
  }, [applicationStatusWorkflows, canAct]);

  return { getAvailableActions };
};
